import { useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, IconButton, Stack, Tab, Tabs,
  Tooltip, Typography,
} from '@mui/material'
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined'
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { TrashedGroup } from '@cmms/api-client'

import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { useConfirm } from '../../components/ConfirmDialog'
import { useOrgSettings } from '../../hooks/useOrgSettings'

const MODULE_ICONS: Record<string, React.ElementType> = {
  work_orders: BuildOutlinedIcon,
  assets: Inventory2OutlinedIcon,
  parts: WarehouseOutlinedIcon,
  vendors: StorefrontOutlinedIcon,
  teams: GroupsOutlinedIcon,
  locations: LocationOnOutlinedIcon,
  asset_categories: CategoryOutlinedIcon,
}

const MODULE_COLORS: Record<string, string> = {
  work_orders: '#3B82F6',
  assets: '#7C3AED',
  parts: '#0D9488',
  vendors: '#EA580C',
  teams: '#059669',
  locations: '#D97706',
  asset_categories: '#8B5CF6',
}

export function TrashTab() {
  const qc = useQueryClient()
  const confirmDlg = useConfirm()
  const { formatDate } = useOrgSettings()
  const [activeModule, setActiveModule] = useState<string | null>(null)

  const trashQ = useQuery({
    queryKey: ['trash'],
    queryFn: () => api.trash.list(),
  })
  const data: Record<string, TrashedGroup> = (trashQ.data ?? {}) as any
  const modules = Object.entries(data)
  const totalCount = modules.reduce((s, [, g]) => s + g.items.length, 0)

  const active = activeModule ?? modules[0]?.[0] ?? null
  const activeGroup = active ? data[active] : null

  const restoreM = useMutation({
    mutationFn: ({ module, id }: { module: string; id: string }) => api.trash.restore(module, id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['trash'] }); toast.success('Item restored') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to restore'),
  })

  const deleteM = useMutation({
    mutationFn: ({ module, id }: { module: string; id: string }) => api.trash.permanentDelete(module, id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['trash'] }); toast.success('Permanently deleted') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to delete'),
  })

  if (trashQ.isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Deleted items</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
            {totalCount > 0 ? `${totalCount} items in trash across ${modules.length} modules. Restore or permanently delete.` : 'Trash is empty — no deleted items.'}
          </Typography>
        </Box>
      </Stack>

      {totalCount === 0 ? (
        <Box sx={{ py: 10, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2.5 }}>
          <DeleteOutlineIcon sx={{ fontSize: 48, color: 'text.disabled', opacity: 0.3, mb: 1.5 }} />
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.secondary' }}>Trash is empty</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.disabled', mt: 0.5 }}>Deleted items will appear here for recovery</Typography>
        </Box>
      ) : (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          {/* Module tabs */}
          <Stack spacing={0.75} sx={{ width: { md: 220 }, flexShrink: 0 }}>
            {modules.map(([key, group]) => {
              const Icon = MODULE_ICONS[key] ?? DeleteOutlineIcon
              const color = MODULE_COLORS[key] ?? '#64748B'
              const isActive = active === key
              return (
                <Box key={key} onClick={() => setActiveModule(key)}
                  sx={{
                    px: 2, py: 1.5, borderRadius: 2, cursor: 'pointer',
                    border: '1px solid', borderColor: isActive ? color : 'divider',
                    bgcolor: isActive ? `${color}08` : 'transparent',
                    transition: 'all 120ms',
                    '&:hover': { borderColor: color },
                  }}>
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Box sx={{ width: 28, height: 28, borderRadius: 99, bgcolor: `${color}14`, color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon sx={{ fontSize: 15 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{group.label}</Typography>
                    </Box>
                    <Chip label={group.items.length} size="small"
                      sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: `${color}14`, color }} />
                  </Stack>
                </Box>
              )
            })}
          </Stack>

          {/* Items list */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {activeGroup && (
              <Stack spacing={1}>
                {activeGroup.items.map((item) => {
                  const name = (item as any)[activeGroup.nameField] ?? '—'
                  const code = activeGroup.codeField ? (item as any)[activeGroup.codeField] : null
                  const color = MODULE_COLORS[active!] ?? '#64748B'

                  return (
                    <Box key={item.id} sx={{ px: 2.5, py: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                      bgcolor: 'background.paper', '&:hover .trash-actions': { opacity: 1 } }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>{name}</Typography>
                            {code && <Chip label={code} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: 'action.hover', color: 'text.secondary', fontFamily: 'ui-monospace, monospace' }} />}
                            {item.status && <Chip label={item.status} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, textTransform: 'capitalize' }} />}
                            {item.priority && <Chip label={item.priority} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, textTransform: 'capitalize', bgcolor: `${color}14`, color }} />}
                          </Stack>
                          <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
                            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
                              Deleted {formatDate(item.deleted_at)}
                            </Typography>
                            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>·</Typography>
                            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
                              Created {formatDate(item.created_at)}
                            </Typography>
                          </Stack>
                        </Box>
                        <Stack className="trash-actions" direction="row" spacing={0.75} sx={{ opacity: 0, transition: 'opacity 120ms' }}>
                          <Tooltip title="Restore" arrow>
                            <Button size="small" variant="outlined" color="success"
                              startIcon={<RestoreOutlinedIcon sx={{ fontSize: 15 }} />}
                              onClick={() => restoreM.mutate({ module: active!, id: item.id })}
                              disabled={restoreM.isPending}
                              sx={{ fontSize: 12, fontWeight: 600, borderRadius: 2 }}>
                              Restore
                            </Button>
                          </Tooltip>
                          <Tooltip title="Delete permanently" arrow>
                            <IconButton size="small"
                              onClick={() => confirmDlg({
                                title: 'Delete permanently?',
                                message: `"${name}" will be permanently removed. This cannot be undone.`,
                                confirmLabel: 'Delete forever',
                                variant: 'danger',
                              }).then((ok) => ok && deleteM.mutate({ module: active!, id: item.id }))}
                              sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' }, border: '1px solid', borderColor: 'divider', borderRadius: 1.25 }}>
                              <DeleteForeverOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Box>
                  )
                })}
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  )
}
